import { useCallback, useState } from "react";
import type {
  ChildExtracurricular,
  ChildFeeAccount,
  ChildFeePayment,
  ChildReportCard,
  ParentChild,
} from "../../../types/parentFamily";

export type FamilyDeleteTarget =
  | { type: "activity"; childId: string; id: string; label: string }
  | { type: "payment"; childId: string; id: string; label: string }
  | {
      type: "fee-account-payment";
      childId: string;
      accountId: string;
      id: string;
      label: string;
    }
  | { type: "fee-account"; childId: string; id: string; label: string }
  | { type: "school-fees"; childId: string; id: string; label: string }
  | { type: "report"; childId: string; id: string; label: string }
  | { type: "member"; childId: string; id: string; label: string };

export function useFamilyModals() {
  const [childModalOpen, setChildModalOpen] = useState(false);
  const [editingChild, setEditingChild] = useState<ParentChild | null>(null);

  const [feeSetupOpen, setFeeSetupOpen] = useState(false);
  const [feeSetupChildId, setFeeSetupChildId] = useState<string | null>(null);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<ChildFeePayment | null>(null);
  const [paymentModalChildId, setPaymentModalChildId] = useState<string | null>(null);

  const [feeAccountPaymentOpen, setFeeAccountPaymentOpen] = useState(false);
  const [feeAccountPaymentChildId, setFeeAccountPaymentChildId] = useState<string | null>(null);
  const [feeAccountPaymentAccountId, setFeeAccountPaymentAccountId] = useState<string | null>(
    null
  );
  const [editingFeeAccountPayment, setEditingFeeAccountPayment] =
    useState<ChildFeePayment | null>(null);

  const [viewLedgerChildId, setViewLedgerChildId] = useState<string | null>(null);

  const [feeAccountModalOpen, setFeeAccountModalOpen] = useState(false);
  const [feeAccountChildId, setFeeAccountChildId] = useState<string | null>(null);
  const [editingFeeAccount, setEditingFeeAccount] = useState<ChildFeeAccount | null>(null);

  const [viewFeeAccountId, setViewFeeAccountId] = useState<string | null>(null);
  const [viewFeeAccountChildId, setViewFeeAccountChildId] = useState<string | null>(null);

  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<ChildExtracurricular | null>(null);
  const [activityModalChildId, setActivityModalChildId] = useState<string | null>(null);

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<ChildReportCard | null>(null);
  const [reportModalChildId, setReportModalChildId] = useState<string | null>(null);

  const [viewingReport, setViewingReport] = useState<ChildReportCard | null>(null);
  const [viewReportChildId, setViewReportChildId] = useState<string | null>(null);

  const [linkGuardianOpen, setLinkGuardianOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FamilyDeleteTarget | null>(null);

  const openChildModal = useCallback((child?: ParentChild | null) => {
    setEditingChild(child ?? null);
    setChildModalOpen(true);
  }, []);

  const openFeeSetup = useCallback((childId: string | null) => {
    setFeeSetupChildId(childId);
    setFeeSetupOpen(true);
  }, []);

  const openRecordPayment = useCallback((childId: string, payment?: ChildFeePayment) => {
    setPaymentModalChildId(childId);
    setEditingPayment(payment ?? null);
    setPaymentModalOpen(true);
  }, []);

  const openFeeAccountPayment = useCallback(
    (childId: string, accountId: string, payment?: ChildFeePayment) => {
      setFeeAccountPaymentChildId(childId);
      setFeeAccountPaymentAccountId(accountId);
      setEditingFeeAccountPayment(payment ?? null);
      setFeeAccountPaymentOpen(true);
    },
    []
  );

  const openAddFeeAccount = useCallback((childId: string) => {
    setFeeAccountChildId(childId);
    setEditingFeeAccount(null);
    setFeeAccountModalOpen(true);
  }, []);

  const openEditFeeAccount = useCallback((childId: string, account: ChildFeeAccount) => {
    setFeeAccountChildId(childId);
    setEditingFeeAccount(account);
    setFeeAccountModalOpen(true);
  }, []);

  const openViewFeeAccount = useCallback((childId: string, accountId: string) => {
    setViewFeeAccountChildId(childId);
    setViewFeeAccountId(accountId);
  }, []);

  const openAddActivity = useCallback((childId: string | null) => {
    setActivityModalChildId(childId);
    setEditingActivity(null);
    setActivityModalOpen(true);
  }, []);

  const openEditActivity = useCallback((childId: string, activity: ChildExtracurricular) => {
    setActivityModalChildId(childId);
    setEditingActivity(activity);
    setActivityModalOpen(true);
  }, []);

  const openAddReport = useCallback((childId: string | null) => {
    setReportModalChildId(childId);
    setEditingReport(null);
    setReportModalOpen(true);
  }, []);

  const openEditReport = useCallback((childId: string, report: ChildReportCard) => {
    setReportModalChildId(childId);
    setEditingReport(report);
    setReportModalOpen(true);
  }, []);

  const openViewReport = useCallback((childId: string, report: ChildReportCard) => {
    setViewReportChildId(childId);
    setViewingReport(report);
  }, []);

  return {
    childModalOpen,
    setChildModalOpen,
    editingChild,
    setEditingChild,
    openChildModal,

    feeSetupOpen,
    setFeeSetupOpen,
    feeSetupChildId,
    setFeeSetupChildId,
    openFeeSetup,

    paymentModalOpen,
    setPaymentModalOpen,
    editingPayment,
    paymentModalChildId,
    openRecordPayment,

    feeAccountPaymentOpen,
    setFeeAccountPaymentOpen,
    feeAccountPaymentChildId,
    feeAccountPaymentAccountId,
    editingFeeAccountPayment,
    openFeeAccountPayment,

    viewLedgerChildId,
    setViewLedgerChildId,

    feeAccountModalOpen,
    setFeeAccountModalOpen,
    feeAccountChildId,
    editingFeeAccount,
    openAddFeeAccount,
    openEditFeeAccount,

    viewFeeAccountId,
    setViewFeeAccountId,
    viewFeeAccountChildId,
    setViewFeeAccountChildId,
    openViewFeeAccount,

    activityModalOpen,
    setActivityModalOpen,
    editingActivity,
    activityModalChildId,
    setActivityModalChildId,
    openAddActivity,
    openEditActivity,

    reportModalOpen,
    setReportModalOpen,
    editingReport,
    reportModalChildId,
    setReportModalChildId,
    openAddReport,
    openEditReport,

    viewingReport,
    setViewingReport,
    viewReportChildId,
    setViewReportChildId,
    openViewReport,

    linkGuardianOpen,
    setLinkGuardianOpen,

    deleteTarget,
    setDeleteTarget,
  };
}
